import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { LogOut, Clock, CheckCircle2, CalendarDays, MapPin, PlayCircle, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { AdmissionForm } from "@/components/AdmissionForm";
import { IdCardButton, AdmitCardButton } from "@/components/StudentDocs";
import { TotpSettings } from "@/components/TotpSettings";
import { EmailVerifyCard } from "@/components/EmailVerifyCard";
import { StudentClassHistory } from "@/components/ClassHistory";
import { useLang } from "@/context/LanguageContext";
import { api } from "@/lib/api";
import { authHeader } from "@/lib/aadhaar";

const Card = ({ children, testid }) => <div className="rounded-3xl border border-slate-200 bg-white p-6" data-testid={testid}>{children}</div>;

const Pending = ({ a }) => {
  const { L } = useLang();
  return (
    <Card testid="admission-pending">
      <div className="flex items-center gap-3"><Clock className="w-6 h-6 text-amber-700" /><h2 className="text-xl font-extrabold text-[#0A192F]">{L("Admission under verification", "दाखिला सत्यापन में है")}</h2></div>
      <p className="mt-2 text-sm text-slate-600">{L("RoboSpark is checking the details and Aadhaar card. You'll get an email once it's approved.", "RoboSpark जानकारी और आधार कार्ड जाँच रहा है। मंज़ूरी मिलते ही ईमेल आएगा।")}</p>
      <div className="mt-4 grid sm:grid-cols-3 gap-3 text-sm">
        <div><span className="text-slate-500">{L("Student", "छात्र")}</span><div className="font-bold text-[#0A192F]">{a.student_name}</div></div>
        <div><span className="text-slate-500">{L("Program", "प्रोग्राम")}</span><div className="font-bold text-[#0A192F]">{a.program}</div></div>
        <div><span className="text-slate-500">Aadhaar</span><div className="font-bold text-[#0A192F] font-mono" data-testid="admission-aadhaar-masked">{a.aadhaar_masked}</div></div>
      </div>
    </Card>
  );
};

const Approved = ({ a }) => {
  const { L } = useLang();
  return (
    <Card testid="admission-approved">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <div className="flex items-center gap-2 text-emerald-700 font-bold text-sm"><CheckCircle2 className="w-5 h-5" />{L("Admission approved", "दाखिला मंज़ूर")}</div>
          <h2 className="mt-1 text-2xl font-extrabold text-[#0A192F]">{a.student_name}</h2>
          <p className="text-sm text-slate-600">{L("Student ID", "छात्र ID")} <b className="text-[#007A8A]" data-testid="student-code">{a.student_code}</b> · {L("Roll no.", "रोल नं.")} <b className="text-[#0A192F]" data-testid="student-roll">{a.roll_no}</b></p>
        </div>
        <IdCardButton a={a} />
      </div>
    </Card>
  );
};

const Exams = ({ a }) => {
  const { L } = useLang();
  const [items, setItems] = useState(null);
  useEffect(() => { api.get("/student/exams", authHeader("student")).then((r) => setItems(r.data.items)).catch(() => setItems([])); }, []);
  return (
    <Card testid="student-exams">
      <h2 className="text-xl font-extrabold text-[#0A192F] flex items-center gap-2"><CalendarDays className="w-5 h-5 text-[#FF2E63]" />{L("Upcoming exams & admit cards", "आने वाली परीक्षाएँ और एडमिट कार्ड")}</h2>
      <div className="mt-4 space-y-3">
        {items === null ? <div className="h-16 rounded-2xl bg-slate-900/5 animate-pulse" /> : items.length === 0
          ? <p className="text-sm text-slate-500" data-testid="student-no-exams">{L("No exams scheduled yet. They will appear here.", "अभी कोई परीक्षा तय नहीं है। यहाँ दिखेगी।")}</p>
          : items.map((e) => (
            <div key={e.id} className="flex flex-wrap items-center justify-between gap-3 p-4 rounded-2xl bg-[#F0F4F8] border border-slate-200" data-testid={`student-exam-${e.id}`}>
              <div>
                <div className="font-bold text-[#0A192F]">{e.title}</div>
                <div className="text-xs text-slate-500 flex flex-wrap gap-3 mt-1"><span>{e.date} · {e.start_time} · {e.duration_min} min</span><span className="inline-flex items-center gap-1"><MapPin className="w-3 h-3" />{e.center}</span></div>
              </div>
              <AdmitCardButton a={a} exam={e} />
            </div>
          ))}
      </div>
    </Card>
  );
};

export default function StudentDashboard() {
  const { L } = useLang();
  const nav = useNavigate();
  const [d, setD] = useState(null);
  const load = useCallback(() => api.get("/student/me", authHeader("student")).then((r) => setD(r.data))
    .catch((e) => {
      if (e.response?.status === 403 && e.response?.data?.detail === "PASSWORD_CHANGE_REQUIRED") nav("/student/login?change=1");
      else if (e.response?.status === 401) { localStorage.removeItem("rs_student_token"); nav("/student/login"); }
    }), [nav]);
  useEffect(() => { if (!localStorage.getItem("rs_student_token")) nav("/student/login"); else load(); }, [load, nav]);
  const logout = () => { localStorage.removeItem("rs_student_token"); api.post("/auth/logout"); nav("/student/login"); };
  const a = d?.admission;

  return (
    <div className="bg-[#E8F0FE] min-h-screen text-[#0A192F]" data-testid="student-dashboard">
      <div className="max-w-5xl mx-auto px-5 lg:px-8 py-10 space-y-6">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#FF2E63]">{L("RoboSpark student portal", "RoboSpark छात्र पोर्टल")}</span>
            <h1 className="text-3xl font-extrabold" data-testid="student-welcome">{L("Namaste", "नमस्ते")}, {d?.user.name || "…"}</h1>
          </div>
          <Button variant="outline" onClick={logout} className="bg-transparent rounded-full" data-testid="student-logout-btn"><LogOut className="w-4 h-4 mr-1" />{L("Logout", "लॉग आउट")}</Button>
        </div>
        {d && !d.user.email_verified && a?.status !== "approved" && <EmailVerifyCard email={d.user.email} onVerified={load} />}
        {d && (!a || a.status === "rejected") && <AdmissionForm user={d.user} prev={a} onDone={(adm) => setD({ ...d, admission: adm })} />}
        {a?.status === "pending" && <Pending a={a} />}
        {a?.status === "approved" && <><Approved a={a} /><Exams a={a} /></>}
        <Card testid="student-classroom-card">
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div className="flex gap-3 items-center">
              <img src="/classroom/zoya-l1.jpg" alt="Zoya Ma'am" className="w-14 h-14 rounded-2xl object-cover ring-2 ring-[#00E5FF]" />
              <div><div className="font-extrabold flex items-center gap-2"><Sparkles className="w-4 h-4 text-[#FF2E63]" />{L("Class with Zoya Ma'am", "ज़ोया मैम की क्लास")}</div><p className="text-sm text-slate-500">{L("8 free lessons, quizzes and live doubt-solving", "8 मुफ़्त पाठ, क्विज़ और सवाल-जवाब")}</p></div>
            </div>
            <Link to="/virtual-classroom"><Button className="rs-cta rounded-full" data-testid="student-classroom-btn"><PlayCircle className="w-4 h-4 mr-1" />{L("Enter classroom", "क्लास में जाएँ")}</Button></Link>
          </div>
        </Card>
        {d && <StudentClassHistory />}
        {d && <TotpSettings role="student" />}
      </div>
    </div>
  );
}
